"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/auth-context";
import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";
import { LogOut, Moon, Pencil, Sun } from "lucide-react";
import { UserAvatar } from "./user-avatar";

const ROL_LABELS: Record<string, string> = {
  admin: "Administrador",
  profesional: "Profesional",
  recepcion: "Recepción",
  marketing: "Marketing",
};

interface UserDropdownProps {
  fotoUrl?: string;
  nombreOverride?: string;
  onEditProfile: () => void;
}

export function UserDropdown({ fotoUrl, nombreOverride, onEditProfile }: UserDropdownProps) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  if (!user) return null;

  const displayNombre = nombreOverride ?? user.nombre;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-2.5 px-3 py-2 w-full rounded-md text-left",
          "hover:bg-sidebar-accent transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        )}
        aria-label="Menú de usuario"
      >
        <UserAvatar nombre={displayNombre} fotoUrl={fotoUrl} />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-sidebar-foreground truncate leading-tight">
            {displayNombre}
          </p>
          <p className="text-[10px] text-sidebar-foreground/50 uppercase tracking-widest font-semibold mt-0.5">
            {ROL_LABELS[user.rol] ?? user.rol}
          </p>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start" sideOffset={4} className="w-52">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="font-normal">
            <p className="text-[12px] font-medium truncate">{displayNombre}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user.username}</p>
          </DropdownMenuLabel>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={toggleTheme}>
          {theme === "dark"
            ? <Sun className="size-4 mr-2" aria-hidden />
            : <Moon className="size-4 mr-2" aria-hidden />}
          {theme === "dark" ? "Modo claro" : "Modo oscuro"}
        </DropdownMenuItem>

        <DropdownMenuItem onClick={onEditProfile}>
          <Pencil className="size-4 mr-2" aria-hidden />
          Editar perfil
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={logout}
          variant="destructive"
        >
          <LogOut className="size-4 mr-2" aria-hidden />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
